/* eslint-disable jsdoc/check-alignment, jsdoc/check-indentation */
import type {IAttributes, IAugmentedJQuery, IParseService, IScope, ITimeoutService, IWindowService} from 'angular';

// @formatter:off
/**
 * @ngdoc directive
 * @name kpOnOverflow
 * @module portaro.directives
 *
 * @restrict A
 * @element ANY
 *
 * @param {expression} kpOnOverflow Expression evaluated if directive detects overflowing on resize event.
 *                                   Element object is available as `$element` parameter. Overflow status is available as `isOverflowed` parameter.
 * @param {expression=} kpOnOverflowApi Expression evaluated in `link` phase. Publishing `$api` as parameter with API reference.
 *
 * @description
 * Directive listening of `window.resize` event and detecting overflow of element.
 * If directive detects oerflow in init phase, it triggers handler.
 * Through `kp-on-overflow-api` attribute it can be accessed API of directive which have following methods:
 *
 * # API
 * | Method name | Description |
 * |-------------|-------------|
 * | `manualCheck(evaluate: boolean): boolean` | Manually triggers overflowing check. <br>`Evaluate` parameter enables expression resolving. If it is `true`, then expression is evaluated. By default it's `true`; <br>Returns `boolean` result (same as `isOverflowed` parameter).|
 *
 * @example
 * <example module="kpOnOverflowExample" name="kpOnOverflowExample" frame-no-resize="true">
    <file name="index.html">
        <main ng-controller="exampleController as ec">
            <div class="first" kp-on-overflow="ec.showAlert($element, $isOverflowed)" kp-on-overflow-api="ec.connectToApi($api)">
                <div class="overflowed">
                    This div should be overflowed
                </div>
            </div>
            <button type="button" ng-click="ec.check()">Check!</button>
        </main>
    </file>

    <file name="script.js">
        angular.module('kpOnOverflowExample', ['portaro.directives'])
            .controller('exampleController', class {
                constructor(){}

               showAlert($element, isOverflowed) {
                   console.log('Element ', $element, 'overflow status: ', isOverflowed);
               }

               connectToApi ($api) {
                   this.overflowApi = $api;
               }

               check() {
                   if(this.overflowApi) {
                       alert(`Overflow status: ${this.overflowApi.manualCheck()}`);
                   }
               }
          });
    </file>
    <file name="style.css">
        .first {
            width: 300px;
            height: 50px;
            border: 1px solid black;
        }

        .overflowed {
            width: 400px;
            height: 40px;
            border: 1px solid red;
        }
    </file>
 * </example>
 */
// @formatter:on

interface OnOverflowDirectiveScope extends IScope {
    isOverflowed: boolean;
}

export default class OnOverflowDirective {
    public static directiveName = 'kpOnOverflow';

    public restrict = 'A';
    public scope = true;

    /*@ngInject*/
    constructor(private $window: IWindowService, private $parse: IParseService, private $timeout: ITimeoutService) {
    }

    public link($scope: OnOverflowDirectiveScope, $element: IAugmentedJQuery, attrs: IAttributes) {
        const onResize = (isManualCheck = false, triggerCallback = true) => {
            const currentOverflow = this.detectOverflow($element[0]);

            if (currentOverflow !== $scope.isOverflowed || isManualCheck) {
                if (triggerCallback) {
                    const rs = this.$parse(attrs[OnOverflowDirective.directiveName]);
                    rs($scope, {$element, $isOverflowed: currentOverflow});
                    $scope.$applyAsync();
                }

                return $scope.isOverflowed = currentOverflow;
            }
        };

        $scope.isOverflowed = false;

        const resolve = this.$parse(attrs.kpOnOverflowApi);
        resolve($scope, {
            $api: {
                manualCheck: onResize.bind(this, true)
            }
        });

        this.$timeout(onResize.bind(this), 0);

        this.$window.addEventListener('resize', onResize.bind(this));
        ['overflow', 'underflow'].forEach((eventName) => $element[0].addEventListener(eventName, onResize.bind(this)));
    }

    /**
     * This function will return you boolean value if the DOM element is overflowed
     * @param {HTMLElement} element
     * @return {boolean}
     */
    private detectOverflow(element) {
        return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
    }
}
